import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Row } from 'react-bootstrap';
import DropFiles from '../content/DropFiles';
import Flex from 'components/common/Flex';
import { filesBase64 } from 'utils/files/filesToBase64';
import { useGetContentMutation } from 'utils/rest/content/getContent';
import { useUploadContentMutation } from 'utils/rest/content/uploadContent';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import { testFilesFormat, testImagesFormat } from 'utils/files/testFilesFormat';

const FurnituresFixturesForm = ({
  typeCategory,
  close,
  prev,
  form,
  imgs,
  docs,
  uri,
  setImagesUri
}) => {
  const [imagesHover, setImagesHover] = useState(false);
  const [docsHover, setDocsHover] = useState(false);
  const [images, setImages] = useState(imgs ?? []);
  const [documents, setDocuments] = useState(docs ?? []);
  const [dragging, setDragging] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [formData, setFormData] = useState(
    form ?? {
      category01ISbb_q4sg_content_categories2ID: typeCategory.category,
      category02ISbb_q4sg_content_categories2ID:
        typeCategory.category01ISbb_q4sg_content_categoriesID,
      category03ISbb_q4sg_content_categories2ID:
        typeCategory.category03ISbb_q4sg_content_categoriesID,
      content01ISbb_q4sg_contentID: ''
    }
  );

  const { mutate: uploadContent, isPending } = useUploadContentMutation();
  const { mutate: getContent } = useGetContentMutation();
  const { user } = useSelector(state => state.auth);
  const { new_tree } = useSelector(state => state.appsetting);

  const sub_dropdowns = useMemo(() => {
    const f_f_dropdown = new_tree?.sub_categories
      ?.find(el => el.name.toLowerCase().includes('shop'))
      .sub_categories.find(el =>
        formData.category
          ? el._id === formData.category
          : el.name.toLowerCase().includes('fixtures')
      ).sub_categories;

    const sub_dropdowns = f_f_dropdown
      ?.find(
        el => el._id === formData?.category02ISbb_q4sg_content_categories2ID
      )
      ?.sub_categories.find(
        el => el._id === formData.category03ISbb_q4sg_content_categories2ID
      )?.sub_categories;

    return sub_dropdowns;
  }, [new_tree, formData]);

  const handleBack = () => {
    prev();
    close();
  };

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const dataToSubmit = { ...formData };

    if (!isChecked) {
      delete dataToSubmit.content01ISbb_q4sg_contentID;
    }

    const dataImg = await filesBase64(dataToSubmit, images, 'img');
    const data = await filesBase64(dataImg, documents, 'doc');

    if (data) {
      await uploadContent(data, {
        onSuccess: () => {
          close();
          getContent(user._id);
        }
      });
    }
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === 'img') {
      setDragging({ images: true, documents: false });
    } else {
      setDragging({ images: false, documents: true });
    }
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = async (e, type) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);

    if (type === 'img') {
      const validated = await testImagesFormat(images, droppedFiles);
      setImages(validated);
    }
    if (type === 'doc') {
      const validated = await testFilesFormat(documents, droppedFiles);
      setDocuments(validated);
    }
  };

  useEffect(() => {
    if (form?.content01ISbb_q4sg_contentID) setIsChecked(true);
  }, [form]);

  return (
    <Form onSubmit={handleSubmit} className="px-6 pt-4">
      <Row>
        <fieldset className={'form-legend-item-wrapper mb-4'}>
          <legend className="item-title fs-1 fw-semi-bold">Fixture Name</legend>
          <Form.Control
            value={formData.name}
            name="name"
            onChange={handleChange}
            required
          />
        </fieldset>
      </Row>
      <Row>
        <fieldset className={'form-legend-item-wrapper mb-4'}>
          <legend className="item-title fs-1 fw-semi-bold">
            Fixture Description
          </legend>
          <Form.Control
            as={'textarea'}
            rows={3}
            value={formData.contentISplaintextbox}
            name="contentISplaintextbox"
            onChange={handleChange}
            required
          />
        </fieldset>
      </Row>
      <div className="d-grid grid-cols-2 row">
        <fieldset className={'form-legend-item-wrapper mb-4'}>
          <legend className="item-title fs-1 fw-semi-bold">Dimensions</legend>
          <Form.Control
            value={formData.dimensions_net}
            name="dimensions_net"
            onChange={handleChange}
            required
          />
        </fieldset>

        <fieldset className={'form-legend-item-wrapper mb-4'}>
          <legend className="item-title fs-1 fw-semi-bold">Materials</legend>
          <Form.Control
            value={formData.materials}
            name="materials"
            onChange={handleChange}
            required
          />
        </fieldset>
      </div>

      <Row>
        <fieldset
          className="form-legend-item-wrapper mb-4 position-relative"
          style={{ zIndex: 50 }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">Images</span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setImagesHover(true)}
              onMouseLeave={() => setImagesHover(false)}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {imagesHover && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.zip</strong> or <strong>image</strong> files
                      are allowed.
                    </li>
                    <li>
                      The <strong>.zip</strong> archive must not contain any
                      folders
                    </li>
                    <li>
                      Accepted image formats: <strong>.png</strong> and{' '}
                      <strong>.jpg</strong>.
                    </li>
                    <li>
                      You can upload up to <strong>9 images</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>
          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'img')}
            onDragLeave={() => handleDragLeave('img')}
            onDrop={e => handleDrop(e, 'img')}
          >
            <DropFiles
              dragging={dragging}
              items={images}
              setFiles={setImages}
              form={formData}
              setForm={setFormData}
              type={'images'}
              uri={uri}
              setImagesUri={setImagesUri}
            />
          </label>
        </fieldset>
      </Row>
      <Row>
        <fieldset
          className={'form-legend-item-wrapper mb-4'}
          style={{ position: 'relative' }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">Documents</span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setDocsHover(true)}
              onMouseLeave={() => setDocsHover(false)}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {docsHover && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.zip</strong> or <strong>docs</strong> files
                      are allowed.
                    </li>
                    <li>
                      The <strong>.zip</strong> archive must not contain any
                      folders
                    </li>
                    <li>
                      Accepted docs formats: <strong>.pdf, .docx</strong> and{' '}
                      <strong>.xlsx</strong>.
                    </li>
                    <li>
                      You can upload up to <strong>9 docs</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>

          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'doc')}
            onDragLeave={() => handleDragLeave('doc')}
            onDrop={e => handleDrop(e, 'doc')}
          >
            <DropFiles
              items={documents}
              setFiles={setDocuments}
              type={'documents'}
            />
          </label>
        </fieldset>
      </Row>

      <Row>
        <Flex
          alignItems={'center'}
          className={`gap-2 ${isChecked ? 'mb-2' : 'mb-4'}`}
        >
          <label>Is this to replace existing content?</label>
          <input
            className="mb-2"
            type="checkbox"
            checked={isChecked}
            onChange={() => setIsChecked(prev => !prev)}
          />
        </Flex>
      </Row>
      {isChecked && (
        <Row>
          <fieldset className={'form-legend-item-wrapper mb-4'}>
            <legend className="item-title fs-1 fw-semi-bold">Link</legend>
            <Form.Control
              as="select"
              required
              className="form-select"
              value={formData.content01ISbb_q4sg_contentID}
              name="content01ISbb_q4sg_contentID"
              onChange={handleChange}
            >
              <option value="" disabled>
                Choose content
              </option>
              {sub_dropdowns?.map((status, index) => (
                <option value={status._id} data-name={status.name} key={index}>
                  {status.name}
                </option>
              ))}
            </Form.Control>
          </fieldset>
        </Row>
      )}
      <Flex className="gap-5">
        <Button variant="primary" type="submit" disabled={isPending}>
          Submit For Approval
        </Button>
        {prev && (
          <Button
            variant="primary"
            type="button"
            onClick={handleBack}
            disabled={isPending}
          >
            Back
          </Button>
        )}
      </Flex>
    </Form>
  );
};

export default FurnituresFixturesForm;
